import { supabase } from './supabase';
import { setCloudStatus } from './cloudStatus';

/**
 * Reads and writes the two site_content rows in Supabase — one holding the
 * draft, one holding what fans see live.
 *
 * This module deliberately knows nothing about the shape of the site content,
 * so data/website.ts stays the single owner of that type.
 */

export type CloudStage = 'draft' | 'live';

export interface CloudRecord {
  content: Record<string, unknown>;
  updatedAt: string;
}

export async function fetchCloudContent(stage: CloudStage): Promise<CloudRecord | null> {
  try {
    const { data, error } = await supabase
      .from('site_content')
      .select('content, updated_at')
      .eq('id', stage)
      .maybeSingle();

    if (error) throw error;
    if (!data?.content) return null;
    return { content: data.content as Record<string, unknown>, updatedAt: String(data.updated_at ?? '') };
  } catch {
    return null;
  }
}

/** Returns an error message, or null when the save reached the database. */
export async function pushCloudContent(stage: CloudStage, content: Record<string, unknown>): Promise<string | null> {
  try {
    const { error } = await supabase
      .from('site_content')
      .upsert({ id: stage, content, updated_at: new Date().toISOString() }, { onConflict: 'id' });

    if (error) throw error;
    return null;
  } catch (error) {
    return describeCloudError(error);
  }
}

/**
 * Supabase reports failures as plain objects rather than Error instances, so
 * the message has to be dug out by hand — otherwise it renders as
 * "[object Object]" and tells nobody anything.
 */
export function readErrorText(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object') {
    const shaped = error as { message?: unknown; error_description?: unknown; details?: unknown; hint?: unknown; code?: unknown };
    const parts = [shaped.message, shaped.error_description, shaped.details, shaped.hint]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0);
    if (parts.length) return parts.join(' · ');
    if (typeof shaped.code === 'string') return `Error code ${shaped.code}`;
    try {
      return JSON.stringify(error);
    } catch {
      return '';
    }
  }
  return String(error);
}

export function describeCloudError(error: unknown): string {
  const raw = readErrorText(error);
  if (/row-level security|policy|permission|denied|not authorized|JWT|401|403/i.test(raw)) {
    return 'Sign in with your emailed link before saving — the database only accepts changes from a signed-in owner. The “Continue without signing in” shortcut cannot save to the cloud.';
  }
  if (/does not exist|relation|schema cache|404/i.test(raw)) {
    return `The database is missing a table this needs. Re-run the setup script in Supabase. (${raw})`;
  }
  if (/failed to fetch|network|offline/i.test(raw)) {
    return 'No connection to the cloud. Your changes are saved on this device — try again once you are back online.';
  }
  return raw ? `Cloud save failed: ${raw}` : 'Something went wrong saving to the cloud.';
}

/** Reports a failed cloud write through the shared status indicator. */
export function reportCloudFailure(error: unknown) {
  setCloudStatus('error', describeCloudError(error));
}
